import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, LogOut, Calendar, ChevronRight, ChevronDown, ChevronUp, Plus, Sparkles, Clock, MessageSquare, AlertTriangle, BookOpen } from "lucide-react";
import TopBar from "@/components/TopBar";
import EventStatusBadge from "@/components/EventStatusBadge";
import EventsFilterBar from "@/components/EventsFilterBar";
import useEventFilters from "@/lib/useEventFilters";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function AdminCommercial() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.role === "super_admin" || user?.role === "admin";

  useEffect(() => {
    Promise.all([
      api.get("/admin/events").then((r) => r.data).catch(() => []),
      api.get("/admin/custom-gift-requests").then((r) => r.data).catch(() => []),
    ]).then(([ev, cr]) => {
      setEvents(ev);
      setRequests(cr);
      setLoading(false);
    });
  }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const upcoming = events.filter((e) => e.status === "upcoming" || e.status === "live");
  const past = events.filter((e) => e.status === "completed" || e.status === "archived");
  const cancelled = events.filter((e) => e.status === "cancelled");
  const totalSubs = events.reduce((acc, e) => acc + (e.subscription_amount || 0), 0);
  const pendingRequests = requests.filter((r) => (r.status || "pending") === "pending").length;
  const alertedEvents = events.filter((e) => e.logistics_alert && e.status !== "cancelled");
  const alertedCount = alertedEvents.length;

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24" data-testid="admin-commercial-page">
      <TopBar />
      <div className="max-w-6xl mx-auto px-5 pt-6">
        {isSuperAdmin && (
          <button
            onClick={() => navigate("/admin")}
            data-testid="commercial-back"
            className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C] mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Tableau de bord
          </button>
        )}

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D81B8C] mb-2">
              Pipeline commercial
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C]">
              Espace Commercial
            </h1>
            <p className="mt-2 text-sm text-[#5C4664]">
              Connecté en tant que <strong>{user?.email}</strong> — rôle <strong>{user?.role}</strong>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/catalog")}
              data-testid="commercial-catalog-link"
              className="rounded-full border-[#F2E2EC] hover:border-[#D81B8C] gap-2"
              title="Catalogue produits (création de cadeaux spéciaux + catalogue global en lecture)"
            >
              <BookOpen className="w-4 h-4" /> Catalogue produits
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="commercial-logout"
              className="rounded-full border-[#F2E2EC] gap-2"
            >
              <LogOut className="w-4 h-4" /> Déconnexion
            </Button>
          </div>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-7" data-testid="commercial-kpis">
          <Kpi label="Événements actifs" value={upcoming.length} accent="#D81B8C" />
          <Kpi label="Événements passés" value={past.length} accent="#6B2C8F" />
          <Kpi label={`Souscriptions ($)`} value={totalSubs.toFixed(0)} accent="#3FA535" />
          <Kpi label="Demandes en attente" value={pendingRequests} accent="#FF8A00" />
          <AlertKpi count={alertedCount} />
        </div>

        {/* Quick actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="commercial-actions">
          <ActionCard
            to="/admin/events/new"
            icon={<Plus className="w-5 h-5" />}
            color="#D81B8C"
            title="Nouvel événement"
            subtitle="Créer un événement et encaisser la souscription"
            testid="commercial-action-new-event"
          />
          <ActionCard
            to="/admin/catalog?special=1"
            icon={<Sparkles className="w-5 h-5" />}
            color="#6B2C8F"
            title="Cadeaux spéciaux"
            subtitle="Gérer les cadeaux uniques / haut de gamme"
            testid="commercial-action-special"
          />
          <ActionCard
            to="/admin/custom-requests"
            icon={<MessageSquare className="w-5 h-5" />}
            color="#FF8A00"
            title="Cadeaux personnalisés"
            subtitle={`${pendingRequests} demande(s) en attente`}
            testid="commercial-action-custom"
          />
        </div>

        {/* Events list */}
        <CommercialUpcomingSection events={upcoming} loading={loading} />

        {past.length > 0 && (
          <div className="mt-10">
            <h2 className="font-display text-xl font-black text-[#2A0E3C] mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" /> Événements passés
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {past.map((ev) => <EventRow key={ev.id} ev={ev} />)}
            </ul>
          </div>
        )}

        {cancelled.length > 0 && (
          <div className="mt-10" data-testid="commercial-cancelled-section">
            <h2 className="font-display text-xl font-black text-red-700 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Événements annulés
              <span className="text-xs font-normal text-[#5C4664]">({cancelled.length})</span>
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 opacity-80">
              {cancelled.map((ev) => <EventRow key={ev.id} ev={ev} />)}
            </ul>
          </div>
        )}

        {/* Last-minute reminder */}
        <div className="mt-10 bg-white rounded-2xl border border-[#FFE2D6] p-5" data-testid="commercial-lm-info">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-[#FF8A00]" />
            <p className="text-xs font-bold uppercase tracking-widest text-[#FF8A00]">
              Cadeaux de dernière minute
            </p>
          </div>
          <p className="text-sm text-[#5C4664]">
            Pour ajouter un cadeau de dernière minute : ouvrez la fiche d&apos;un événement (entre J-48h
            et J-6h) et utilisez le bloc <em>« Dernière minute »</em>. Vous obtiendrez un lien
            WhatsApp privé à transmettre au demandeur.
          </p>
        </div>
      </div>
    </div>
  );
}

function CommercialUpcomingSection({ events, loading }) {
  const alertedTotal = events.filter((e) => e.logistics_alert).length;
  const f = useEventFilters(events);
  return (
    <div className="mt-10">
      <h2 className="font-display text-xl font-black text-[#2A0E3C] mb-3">
        Événements à venir{" "}
        <span className="text-[#5C4664] font-normal text-base">({events.length})</span>
      </h2>
      {loading ? (
        <p className="text-sm text-[#5C4664]">Chargement…</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-[#5C4664]">Aucun événement à venir.</p>
      ) : (
        <>
          <EventsFilterBar
            search={f.search} setSearch={f.setSearch}
            alertOnly={f.alertOnly} setAlertOnly={f.setAlertOnly}
            range={f.range} setRange={f.setRange}
            alertCount={alertedTotal}
            totalCount={events.length}
            filteredCount={f.filtered.length}
            className="mb-4"
          />
          {f.filtered.length === 0 ? (
            <p data-testid="commercial-upcoming-no-match" className="text-sm text-[#5C4664]">
              Aucun événement ne correspond à ce filtre.
            </p>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {f.visible.map((ev) => <EventRow key={ev.id} ev={ev} />)}
            </ul>
          )}
          {f.hiddenCount > 0 && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => f.setExpanded(true)}
                data-testid="commercial-upcoming-expand"
                className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-white border border-[#F2E2EC] text-sm font-semibold text-[#2A0E3C] hover:border-[#D81B8C]"
              >
                Voir tous ({f.filtered.length}) <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}
          {f.expanded && f.filtered.length > f.pageSize && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => f.setExpanded(false)}
                data-testid="commercial-upcoming-collapse"
                className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-semibold text-[#5C4664] hover:text-[#2A0E3C]"
              >
                Réduire <ChevronUp className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#F2E2EC] p-4">
      <p className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664]">{label}</p>
      <p className="font-display text-2xl font-black mt-1" style={{ color: accent }}>{value}</p>
    </div>
  );
}

function AlertKpi({ count }) {
  const active = count > 0;
  return (
    <div
      data-testid="commercial-kpi-alerts"
      className={`relative rounded-2xl p-4 transition-colors ${
        active
          ? "bg-red-50 border-2 border-red-500"
          : "bg-white border border-[#F2E2EC]"
      }`}
    >
      <div className="flex items-center gap-2">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664]">
          Événements en alerte
        </p>
        {active && (
          <span
            data-testid="commercial-alert-dot"
            className="relative inline-flex h-2.5 w-2.5"
            aria-label="Alerte logistique active"
          >
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-600" />
          </span>
        )}
      </div>
      <p
        className={`font-display text-2xl font-black mt-1 ${active ? "text-red-600" : "text-[#5C4664]"}`}
      >
        {count}
      </p>
      {active && (
        <p className="text-[11px] text-red-600 mt-1 leading-tight">
          Au moins un cadeau n&apos;est pas <strong>stocké</strong> à J-6h.
        </p>
      )}
    </div>
  );
}

function ActionCard({ to, icon, color, title, subtitle, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="bg-white rounded-2xl border border-[#F2E2EC] p-5 hover:border-[#D81B8C]/50 transition-colors group"
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
        style={{ backgroundColor: `${color}15`, color }}
      >
        {icon}
      </div>
      <h3 className="font-display text-lg font-black text-[#2A0E3C]">{title}</h3>
      <p className="text-sm text-[#5C4664] mt-1">{subtitle}</p>
      <ChevronRight className="w-4 h-4 text-[#5C4664] group-hover:text-[#D81B8C] mt-3" />
    </Link>
  );
}

function EventRow({ ev }) {
  const alert = !!ev.logistics_alert;
  return (
    <li>
      <Link
        to={`/admin/events/${ev.id}`}
        data-testid={`commercial-event-${ev.id}`}
        data-alert={alert ? "true" : "false"}
        className={`block bg-white rounded-2xl p-5 transition-colors ${
          alert
            ? "border-2 border-red-500 shadow-[0_0_0_4px_rgba(220,38,38,0.08)] hover:shadow-[0_0_0_6px_rgba(220,38,38,0.15)]"
            : "border border-[#F2E2EC] hover:border-[#D81B8C]/50"
        }`}
      >
        <div className="flex justify-between gap-3 mb-1">
          <h3 className="font-display text-lg font-black text-[#2A0E3C] line-clamp-1">{ev.title}</h3>
          <EventStatusBadge status={ev.status} />
        </div>
        <p className="text-sm text-[#5C4664]">{ev.host_names}</p>
        {alert && (
          <div
            data-testid={`commercial-event-${ev.id}-alert`}
            className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs font-semibold text-red-700"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>
              {ev.late_orders_count} cadeau(x) en retard — pas encore stocké à J-6h
            </span>
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-[#5C4664] mt-3">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {ev.event_date}
          </span>
          <span className="font-mono font-bold text-[#D81B8C]">{ev.code}</span>
        </div>
      </Link>
    </li>
  );
}
